import { useEffect } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import {
  fetchCartItem,
  fetchMaterialPriceAndImagesForCart,
  updateCurCartItemSize,
} from '../../services';
import {
  clearSizeError,
  resetTaxEstimate,
  setTaxEstimateResult,
  updateGuestCartItemSize,
  updateTotalAmount,
} from '../../redux/slices/shoppingCartSlice';
import { US_STATE_SALES_TAX_RATES } from '../../constants/usStateSalesTaxRates';
import type { CartItemValidationIssue, ICartItem, MaterialPriceAndImgForCart } from '../interfaces';

export const useCartValidationCleanup = ({
  cartItems,
  cartValidationIssues,
  userId,
}: {
  cartItems: ICartItem[];
  cartValidationIssues: CartItemValidationIssue[];
  userId?: string;
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const issueByItemId = new Map(cartValidationIssues.map((issue) => [issue.itemId, issue]));

    cartItems.forEach((item) => {
      const issue = issueByItemId.get(item.id);

      if (!issue && item.sizeErrors?.length) {
        dispatch(clearSizeError(item.id));
        return;
      }

      const shouldClearInvalidSize =
        item.selectedSize &&
        issue &&
        (issue.field === 'size' ||
          (issue.field === 'stock' &&
            (issue.message.includes('unavailable') || issue.message.includes('out of stock'))));

      if (!shouldClearInvalidSize) return;

      if (userId) {
        dispatch(updateCurCartItemSize({ cartItemId: item.id, newSize: '' }));
      } else {
        dispatch(updateGuestCartItemSize({ itemId: item.id, selectedSize: '' }));
      }
      dispatch(clearSizeError(item.id));
    });
  }, [cartItems, cartValidationIssues, dispatch, userId]);
};

export const useCartTotalAndTaxEstimate = ({
  cartItems,
  materialPriceAndImgForCart,
  province,
  totalAmount,
}: {
  cartItems: ICartItem[];
  materialPriceAndImgForCart: MaterialPriceAndImgForCart[];
  province?: string;
  totalAmount: number;
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (cartItems.length > 0 && materialPriceAndImgForCart.length > 0) {
      dispatch(updateTotalAmount({ cartItems, materialPriceAndImgForCart }));
    }
  }, [cartItems, dispatch, materialPriceAndImgForCart]);

  useEffect(() => {
    if (!cartItems.length || totalAmount <= 0) {
      dispatch(resetTaxEstimate());
      return;
    }

    const manualProvince = String(province || '').trim();
    if (!manualProvince) {
      dispatch(resetTaxEstimate());
      return;
    }

    const stateRate = US_STATE_SALES_TAX_RATES[manualProvince] ?? 0;
    dispatch(
      setTaxEstimateResult({
        rate: stateRate,
        amount: Number((totalAmount * stateRate).toFixed(2)),
      })
    );
  }, [cartItems.length, dispatch, province, totalAmount]);
};

export const useCartMaterialPricingFetch = ({
  cartItems,
  selectedMaterialsRef,
}: {
  cartItems: ICartItem[];
  selectedMaterialsRef: React.MutableRefObject<Record<string, string>>;
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const updates: Record<string, string> = {};

    cartItems.forEach((item) => {
      if (selectedMaterialsRef.current[item.id] === item.selectedMaterial) return;
      if (!item.product?.id || !item.selectedMaterial) return;

      dispatch(fetchMaterialPriceAndImagesForCart({
        itemId: item.id,
        productId: item.product.id,
        selectedMaterial: item.selectedMaterial,
      }));

      updates[item.id] = item.selectedMaterial;
    });

    selectedMaterialsRef.current = {
      ...selectedMaterialsRef.current,
      ...updates,
    };
  }, [cartItems, dispatch, selectedMaterialsRef]);
};

export const useFetchUserCart = (userId?: string) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userId) {
      dispatch(fetchCartItem(userId));
    }
  }, [dispatch, userId]);
};
